/**
 * Wind speed to color mapping (Beaufort-scale inspired)
 * Returns RGBA values for particle rendering based on wind speed in mph.
 */

export interface WindColor {
  r: number
  g: number
  b: number
  a: number
}

// Color stops: [maxSpeed, r, g, b]
export const COLOR_STOPS: [number, number, number, number][] = [
  [5,   97,  200, 238],  // calm: light blue
  [10,  57,  190, 200],  // light: teal
  [15,  80,  220, 140],  // gentle: green-cyan
  [20,  160, 230, 80],   // moderate: yellow-green
  [25,  230, 210, 50],   // fresh: yellow
  [30,  245, 160, 40],   // strong: orange
  [40,  235, 80,  50],   // near-gale: red-orange
  [50,  200, 40,  80],   // gale: red-magenta
  [Infinity, 180, 30, 160], // storm+: magenta
]

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

export function getWindColor(speedMph: number): WindColor {
  const speed = Math.max(0, speedMph)

  let prevStop = COLOR_STOPS[0]
  let prevMax = 0

  for (const stop of COLOR_STOPS) {
    if (speed <= stop[0]) {
      const range = stop[0] === Infinity ? 20 : stop[0] - prevMax
      const t = Math.min(1, (speed - prevMax) / range)
      return {
        r: Math.round(lerp(prevStop[1], stop[1], t)),
        g: Math.round(lerp(prevStop[2], stop[2], t)),
        b: Math.round(lerp(prevStop[3], stop[3], t)),
        a: lerp(0.6, 1.0, Math.min(1, speed / 30)),
      }
    }
    prevMax = stop[0]
    prevStop = stop
  }

  // Fallback (shouldn't reach here)
  const last = COLOR_STOPS[COLOR_STOPS.length - 1]
  return { r: last[1], g: last[2], b: last[3], a: 1.0 }
}

/**
 * Returns a CSS rgba string for a given wind speed.
 */
export function getWindColorString(speedMph: number): string {
  const c = getWindColor(speedMph)
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})`
}
